/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';

/**
 *
 * Displays the list of projects for an organization.
 *
 *
 * ```
 * <Page::Projects::List
 *   @currentPageSize={{this.model.currentPageSize}}
 *   @lastOpenedProject={{this.model.lastOpenedProject}}
 *   @onPageSizeChange={{this.onPageSizeChange}}
 *   @organization={{this.model.organization}}
 *   @pagination={{this.model.pagination}}
 *   @projects={{array project1 project2}}
 * />
 * ```
 *
 * @class PageProjectsList
 *
 */

/**
 * The current page size for paginating the list.
 * @argument currentPageSize
 * @type {Number}
 */

/**
 * The project to render in the card above the project list.
 * @argument lastOpenedProject
 * @type {HashicorpCloudResourcemanagerProject}
 */

/**
 * The function to call when page size changes.
 * @argument onPageSizeChange
 * @type {Function}
 */

/**
 * The current organization.
 * @argument organization
 * @type {Object}
 */

/**
 * The api response's pagination response to render paging controls.
 * @argument pagination
 * @type {HashicorpCloudCommonPaginationResponse}
 */

/**
 * The current organization's projects list.
 * @argument projects
 * @type {Array<HashicorpCloudResourcemanagerProject>}
 */

export default class PageProjectsListComponent extends Component {
  @service router;
  @service analytics;

  get preventDefaultSorting() {
    return () => {
      return 0;
    };
  }

  @action
  async onSortClick(sortBy, sortOrder) {
    this.analytics.trackEvent(
      `external_ui_hashicorp_project_${sortBy}_sort_${sortOrder}`
    );

    await this.router.transitionTo({
      queryParams: {
        orderby: [`${sortBy} ${sortOrder}`],
        next: undefined,
        prev: undefined,
      },
    });
  }

  @action
  handlePageChange(page) {
    this.analytics.trackEvent(
      `external_ui_hashicorp_projects_pagination_${page}`
    );
  }

  @action
  async handleOnPageSizeChanged(pageSize) {
    await this.router.transitionTo({
      queryParams: {
        size: pageSize,
        next: undefined,
        prev: undefined,
      },
    });
  }

  get paginationQueryFunction() {
    const { nextPageToken, previousPageToken } = this.args?.pagination ?? {};

    return (page) => {
      if (page === 'next') {
        return {
          next: nextPageToken,
          prev: undefined,
        };
      } else {
        return {
          prev: previousPageToken,
          next: undefined,
        };
      }
    };
  }
}
